<script setup lang="ts">
import { usePrevious } from "@vueuse/core";
import { computed, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import actions from "~/actions";
import ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import EmptyState from "~/components/dumb/EmptyState.vue";
import IconOrEmojiPicker from "~/components/dumb/IconOrEmojiPicker.vue";
import Input from "~/components/dumb/Input.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import Toggle from "~/components/dumb/Toggle.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import UpgradeConfirmationDialog from "~/components/dumb/UpgradeConfirmationDialog.vue";
import ChatAndSkillEditor from "~/components/text/ChatAndSkillEditor.vue";
import { emojiAllowsSkinTone, getEmojiWithSkinTone } from "~/constants/iconAndEmoji";
import { DotsHorizontalIcon, PlusIcon, SkillIcon, TrashIcon } from "~/icons";
import { getQueryParam, makeLinkToSkillSettingsRef } from "~/router/common";
import {
  ButtonSize,
  DialogMode,
  DropdownMenuItemKind,
  EmojiSkinTone,
  IconKind,
  Placement,
  Subscription,
} from "~/shared/enums";
import type { Skill, SkillUpdate } from "~/shared/types";
import { useAppStore, useDataStore, useTenantStore } from "~/stores";
import { isLexicalStateEmpty } from "~/utils/common";
import { getOrdersBetween } from "~/utils/orderManager";
import { getEmojiRecommendation } from "~/utils/recommendation";
import { when } from "~/utils/wait";

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const tenantStore = useTenantStore();

const dialog = ref<InstanceType<typeof ConfirmationDialog> | null>(null);
const upgradeDialog = ref<InstanceType<typeof UpgradeConfirmationDialog> | null>(null);

const skill = computed<Skill | undefined>(() => {
  const skillDuid = getQueryParam("skill");
  if (!skillDuid) {
    return undefined;
  }
  return dataStore.getSkillByDuid(skillDuid);
});

const previousSkill = usePrevious(skill);

const title = ref(skill.value?.title ?? "");
const titleInput = ref<InstanceType<typeof Input> | null>(null);

const isValidName = (value: string) => ({ isValid: value.trim() !== "", error: "Skill name cannot be empty" });

const openUpgradeDialogMaybe = (): boolean => {
  if (!tenantStore.isSubscriptionGte(Subscription.APP_SUMO)) {
    upgradeDialog.value?.openModal();
    return true;
  }
  return false;
};

const finalizeTitleAndEmoji = async () => {
  if (!skill.value || !titleInput.value || openUpgradeDialogMaybe()) {
    return;
  }

  const skillDuid = skill.value.duid;
  const newTitle = titleInput.value.value;

  if (!isValidName(newTitle).isValid) {
    title.value = skill.value?.title ?? "";
    return;
  }

  if (skill.value.iconKind !== IconKind.NONE) {
    dataStore.updateSkill({ duid: skillDuid, title: newTitle });
    return;
  }

  const emojiRecUpdate = await getEmojiRecommendation(skillDuid, newTitle);
  dataStore.updateSkill({
    duid: skillDuid,
    title: newTitle,
    ...emojiRecUpdate,
  });
};

const focusTitle = () => {
  if (!titleInput.value) {
    return;
  }
  titleInput.value.focus();
  titleInput.value.select();
};

const createSkill = async () => {
  if (openUpgradeDialogMaybe()) {
    return;
  }

  const newSkill = await dataStore.createSkill(
    "Skill",
    getOrdersBetween(undefined, dataStore.skillList({ includeHidden: true })[0]?.order)[0]
  );

  if (!newSkill) {
    return;
  }

  router.replace(makeLinkToSkillSettingsRef(newSkill.duid).value);
  await when(titleInput);
  focusTitle();
};

const skillSections = computed(() => (skill.value ? actions.context.skill(skill.value, dialog.value) : []));

const toggleSkillVisibility = (isVisible: boolean) => {
  if (openUpgradeDialogMaybe()) {
    return;
  }

  if (!skill.value) {
    return;
  }
  dataStore.updateSkill({ duid: skill.value.duid, hidden: !isVisible });
};

const onDelete = () => {
  if (openUpgradeDialogMaybe()) {
    return;
  }

  const currSkill = skill.value ?? previousSkill.value;
  if (!currSkill) {
    return;
  }

  dataStore.deleteSkill(currSkill);
  router.replace(makeLinkToSkillSettingsRef(dataStore.skillList({ includeHidden: true })[0]?.duid).value);
};

const iconPicker = ref<InstanceType<typeof DropdownMenu> | null>(null);
const iconPickerSections = computed(() =>
  !skill.value
    ? []
    : [
        {
          title: "Modify",
          items: [
            {
              title: "Icons",
              kind: DropdownMenuItemKind.COMPONENT,
              noFocus: true,
              component: IconOrEmojiPicker,
              componentArgs: {
                iconKind: skill.value.iconKind,
                iconNameOrEmoji: skill.value.iconNameOrEmoji,
                colorHex: skill.value.colorHex,
                onSelectColor: (colorHex: string) => {
                  if (!skill.value) {
                    return;
                  }
                  dataStore.updateSkill({ duid: skill.value.duid, colorHex });
                },
                onSelectSkinTone: (skinTone: EmojiSkinTone) => {
                  if (!skill.value) {
                    return;
                  }
                  appStore.setSkinTone(skinTone);

                  if (!emojiAllowsSkinTone(skill.value.iconNameOrEmoji)) {
                    return;
                  }

                  dataStore.updateSkill({
                    duid: skill.value.duid,
                    iconNameOrEmoji: getEmojiWithSkinTone(skill.value.iconNameOrEmoji, skinTone),
                  });
                },
                onSelectIconOrEmoji: (iconKind: IconKind, iconNameOrEmoji: string) => {
                  if (!skill.value) {
                    return;
                  }
                  const { duid } = skill.value;
                  const update: SkillUpdate = { duid, iconNameOrEmoji };

                  if (iconKind !== skill.value.iconKind) {
                    update.iconKind = iconKind;
                  }

                  dataStore.updateSkill(update);
                },
                onAfterSelect: () => {
                  iconPicker.value?.close();
                },
              },
            },
          ],
        },
      ]
);

const deleteSkillIfEmpty = (skillToDelete: Skill) => {
  if (isLexicalStateEmpty(skillToDelete.prompt)) {
    dataStore.deleteSkill(skillToDelete);
  }
};

watch(
  () => skill.value,
  (newSkill, prevSkill) => {
    title.value = newSkill?.title ?? "";
    if (!newSkill || !prevSkill || newSkill.duid === prevSkill.duid) {
      return;
    }

    deleteSkillIfEmpty(prevSkill);
  },
  { immediate: true }
);

onUnmounted(() => {
  if (skill.value) {
    deleteSkillIfEmpty(skill.value);
  }
});

defineExpose({
  focusTitle,
});
</script>

<template>
  <div class="contents">
    <UpgradeConfirmationDialog
      v-if="!tenantStore.isSubscriptionGte(Subscription.APP_SUMO)"
      ref="upgradeDialog"
      feature-action="use AI skills"
      :subscription="Subscription.PREMIUM"
      no-button />

    <EmptyState
      v-if="dataStore.skillList({ includeHidden: true }).length === 0"
      title="No skills"
      description="This workspace doesn't have any skills yet. Get started by creating a new one."
      :main-icon="SkillIcon"
      button-text="Create skill"
      :button-icon="PlusIcon"
      @click="createSkill" />

    <div v-else-if="skill" class="mt-10 flex flex-1 flex-col gap-10 overflow-y-auto px-8 pb-72 lg:px-16">
      <div class="flex items-center gap-2">
        <span class="flex-1 select-none text-base text-md">Edit skill</span>

        <div class="flex items-center gap-2">
          <ConfirmationDialog
            ref="dialog"
            :mode="DialogMode.DELETE"
            :title="`Delete ${skill.title}`"
            description="Are you sure you want to delete this AI skill? This action cannot be undone."
            confirm-text="Delete"
            cancel-text="Keep"
            :icon="TrashIcon"
            @confirm="onDelete" />

          <DropdownMenu :sections="skillSections" :placement="Placement.BOTTOM_RIGHT" :distance="0" :width-pixels="180">
            <div class="rounded text-lt hover:bg-lt">
              <span class="sr-only">Manage skill</span>
              <DotsHorizontalIcon class="icon-md" />
            </div>
          </DropdownMenu>
        </div>
      </div>

      <!-- Skill name & icon -->
      <div class="flex flex-col text-sm text-md">
        <div class="flex flex-col gap-6">
          <div class="flex w-full items-center gap-1">
            <DropdownMenu
              ref="iconPicker"
              :sections="iconPickerSections"
              :distance="0"
              :width-pixels="383"
              :disabled="!tenantStore.isSubscriptionGte(Subscription.APP_SUMO)"
              @click="openUpgradeDialogMaybe">
              <Tooltip text="Change icon">
                <span
                  class="mb-5 flex items-center justify-center rounded border p-1 bg-std border-hvy hover:bg-opposite/10">
                  <PageIcon :page="skill" />
                </span>
              </Tooltip>
            </DropdownMenu>

            <Input
              ref="titleInput"
              :init-value="title"
              class="w-full"
              is-form
              placeholder="Skill name"
              label="Name"
              :validate="isValidName"
              @focus="openUpgradeDialogMaybe"
              @finalize="finalizeTitleAndEmoji" />
          </div>
          <Toggle
            class="w-full"
            :size="ButtonSize.SMALL"
            :value="!skill.hidden"
            label="Enable"
            text-sm
            text-lt
            description="When disabled, this skill does not appear as an option"
            @update="toggleSkillVisibility" />

          <div class="contents" @click="openUpgradeDialogMaybe" @keydown="openUpgradeDialogMaybe">
            <ChatAndSkillEditor ref="skillPromptEditor" :skill="skill" editor-classes="p-1 px-2" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
